// Reverse white / warm white on a Wi-Fi light — the same switch the BLE lamps
// have (BleNode.SwapWarm, core/ble/lampState), for DeviceIDs 25, 90 (wwh) and 23
// (wwrgb). Some fixtures are wired with the two LED strings the other way round,
// so "White" lights warm. It is a fact of the INSTALLED unit, so it lives on the
// install row:  DevicesInstalled.SwapWarm  0 = normal (default), 1 = reversed.
// Column added by Home Automation light_swap_warm_migration.sql (run once).
//
// ⚠️ THE SCREEN NEVER SHOWS THE SWAP. Sliders, presets and scene values are what
// the user sees; only the two values on the WIRE are exchanged — on the way out
// (publish) and on the way in (the status echo on <uno>/client), so a reversed
// light's sliders still read White = white. `toWire` and `fromWire` are the same
// exchange, which is why each is its own inverse.
//
// RGB (24) has no white channels and is never reversed.
//
// ⚠️ NEWER FIRMWARE DOES THE SWAP ITSELF, AND THEN THE APP MUST NOT. The flag now
// also lives in the DEVICE — `SWAPWARMON`/`OFF`, kept in EEPROM[14] on
// OtaForTuya and NVS `swap_warm` on the C3, loaded at boot and overwritten by
// the `SwapWarm` column when `VerifyDeviceNew` answers (core/devices/commands).
// That is what the curtain's reverse has always done, and it is better: the wall
// switch, a scene run by the SERVER and Alexa are corrected too, and so is a
// phone driving the light over Bluetooth with no server in reach.
//
// **Two swaps are the same as none**, so exactly one side must do it:
// `appMustSwap` is the fixture's flag XOR what the device says ITS flag is
// (`chipSwapsWhite`, asked once per session). Until it answers, the app swaps,
// because that is what every unit in the field does today and it is right for
// all of them.
//
// ⚠️ ONLY A REVERSED LIGHT IS EVER ASKED. With the flag at 0 neither side swaps
// anything, so the question does not arise — which is why this costs one probe
// on the handful of reversed fixtures and nothing at all on the rest.
//
// ⚠️ The room-device lists do not carry SwapWarm: it is read from the full
// install row once per device and cached for the session (as glassFinish).

import { getDeviceDetail, getDeviceInstalledById, updateDeviceInstalled } from '../api/api';
import { CMD_SWAP_WARM_ON, CMD_SWAP_WARM_OFF, CMD_GET_SWAP_WARM } from './commands';
import { askDeviceFlag, setDeviceFlag } from './deviceFlagProbe';

/** The column, and the name the firmware answers with. */
export const SWAP_COLUMN = 'SwapWarm';

const cache = new Map();        // uno -> { swapped, ownerId }
const inflight = new Map();     // uno -> Promise
const listeners = new Set();

/** True when this channel count has a white + warm pair at 0 and 1 (wwh 2, wwrgb 5). */
export const hasWhitePair = (count) => count === 2 || count === 5;

/** Exchange channels 0 and 1 when reversed. Same call both ways. */
export function swapWhite(values, swapped) {
  if (!swapped || !Array.isArray(values) || !hasWhitePair(values.length)) return values;
  const v = [...values];
  [v[0], v[1]] = [v[1], v[0]];
  return v;
}
export const toWire = swapWhite;
export const fromWire = swapWhite;

/** The same exchange on a "a,b,…" message (a scene step). Non-channel text passes through. */
export function swapMessage(message, swapped) {
  const parts = String(message ?? '').split(',');
  if (!swapped || !hasWhitePair(parts.length) || parts.some((p) => !/^\s*\d+\s*$/.test(p))) return message;
  return swapWhite(parts.map((p) => p.trim()), true).join(',');
}

export const rowSwapped = (row) => Number(row && row.SwapWarm) === 1;

/** Cached flag, or undefined when not read yet. */
export const cachedReversed = (uno) => cache.get(String(uno))?.swapped;

/** Cached owner (DevicesInstalled.MasterUserId), or undefined. */
export const cachedOwner = (uno) => cache.get(String(uno))?.ownerId;

/** -> boolean. Never throws; false when it cannot be read. */
export function getLightReversed(device) {
  const uno = String(device && device.UniqueNumber);
  if (cache.has(uno)) return Promise.resolve(cache.get(uno).swapped);
  if (inflight.has(uno)) return inflight.get(uno);
  const p = (async () => {
    try {
      let id = device && device.ID;
      if (id == null) {
        const res = await getDeviceDetail(uno);
        id = (Array.isArray(res.data) ? res.data[0] : null)?.ID;
      }
      if (id == null) return false;
      const res = await getDeviceInstalledById(id);
      const row = Array.isArray(res.data) ? res.data[0] : null;
      const entry = { swapped: rowSwapped(row), ownerId: row ? row.MasterUserId : undefined };
      cache.set(uno, entry);
      listeners.forEach((fn) => { try { fn(uno, entry.swapped); } catch (e) { /* a listener's fault */ } });
      return entry.swapped;
    } catch (e) {
      return false;
    } finally {
      inflight.delete(uno);
    }
  })();
  inflight.set(uno, p);
  return p;
}

/** Read several at once (the scene editor needs them before it converts). */
export const loadReversed = (devices) => Promise.all((devices || []).map((d) => getLightReversed(d)));

/** Screens re-draw when a flag changes or is first read. -> unsubscribe */
export function onReverseChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Which side swaps, and the one rule that decides it ─────────────────
//
//     the app exchanges the channels  ⇔  (the fixture is reversed)
//                                         XOR (the chip is already doing it)
//
// ⚠️ IT IS THE CHIP'S REPORTED VALUE, NOT "DOES THIS FIRMWARE HAVE THE FLAG".
// The weaker test was wrong in a case that will happen: the flag is set while
// the light is powered down, so the column says 1 and the chip still says 0.
// Asking only "did it answer" would have stopped the app swapping while nothing
// else had started, and the fixture would light warm for White — the exact
// fault the whole feature exists to remove.
//
// The XOR also handles the mirror case for free: a chip left holding 1 while
// the column has gone back to 0 is compensated for by the app until the word
// gets through. In both directions the LIGHT is right while the two sides
// disagree, and `healChipSwap` sends the word again to end the disagreement.
//
// ⚠️ THE SERVER CANNOT CURRENTLY HEAL IT. `VerifyDeviceNew` returns
// `ReverseAction` (verified against the live API, 2026-10-23) but NOT
// `SwapWarm`, so a WWH light cannot learn this flag the way a curtain learns
// its direction — the word is the only route in. Add `SwapWarm` to that SELECT
// and this becomes self-correcting without the app.
const chipSwap = new Map();     // uno -> 0 | 1   (what the DEVICE reports)
const chipAsk = new Map();      // uno -> Promise<0|1|undefined>
const asked = new Set();        // probed in THIS session
const healed = new Set();       // re-sent the word in THIS session

// ── Why a 1 is remembered across launches and a 0 is not ───────────────────
// Over Bluetooth there is no broker to ask (ui/mobile/components/BleDevicePanel),
// so without a remembered answer the phone would swap on top of a chip that had
// already swapped — offline, where nothing can correct it. The asymmetry is
// deliberate: a remembered 1 is wrong only if a device is DOWNGRADED or has
// been set back by another phone, which is rare and self-corrects at the next
// online probe (which overwrites the entry); a remembered 0 would be wrong
// after every OTA, which is routine.
const CHIP_KEY = 'sli.light.chipSwap';
const readStore = () => {
  try { return JSON.parse(localStorage.getItem(CHIP_KEY)) || {}; } catch (e) { return {}; }
};
function rememberChip(uno, value) {
  try {
    const all = readStore();
    if (value === 1) all[uno] = 1; else delete all[uno];
    localStorage.setItem(CHIP_KEY, JSON.stringify(all));
  } catch (e) { /* private mode / blocked storage — the session cache still holds */ }
}
function seedFromStore() {
  try {
    Object.keys(readStore()).forEach((uno) => chipSwap.set(uno, 1));
  } catch (e) { /* see rememberChip */ }
}
seedFromStore();

/** What the device last said its own flag is: 0, 1, or undefined if unasked. */
export const cachedChipSwaps = (uno) => chipSwap.get(String(uno));

/**
 * Send the word again when the chip and the column disagree. Fire and forget,
 * once per device per session — the screen is already behaving correctly
 * (see the XOR above), so this is repair, not a step the user waits for.
 */
function healChipSwap(uno, want) {
  if (healed.has(uno)) return;
  healed.add(uno);
  setDeviceFlag(uno, want ? CMD_SWAP_WARM_ON : CMD_SWAP_WARM_OFF, CMD_GET_SWAP_WARM, SWAP_COLUMN)
    .then((r) => {
      if (!r.answered) return;
      chipSwap.set(uno, r.value);
      rememberChip(uno, r.value);
      listeners.forEach((fn) => {
        try { fn(uno, cache.get(uno)?.swapped); } catch (e) { /* a listener's fault */ }
      });
    })
    .catch(() => {});
}

/**
 * Ask the device what its own flag is. -> Promise<0 | 1 | undefined>
 *
 * `undefined` is "could not be established" — silence, no broker, or firmware
 * with no such flag, which are indistinguishable and all mean the same thing
 * here: the app must carry on swapping, as every unit in the field does today.
 *
 * Asked once per device per session. Not cached longer than that for a 0,
 * because an OTA can give a light this flag between two launches.
 */
export function chipSwapsWhite(uniqueNumber) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return Promise.resolve(undefined);
  // ⚠️ THE IN-FLIGHT CHECK COMES FIRST. `asked` is set when the probe STARTS, so
  // testing it first would hand a second caller the not-yet-known value.
  if (chipAsk.has(uno)) return chipAsk.get(uno);
  // ⚠️ `asked`, not `chipSwap.has()` — a remembered 1 seeds the map, and
  // short-circuiting on it would mean a device set back elsewhere is never
  // re-asked.
  if (asked.has(uno)) return Promise.resolve(chipSwap.get(uno));
  asked.add(uno);
  const p = askDeviceFlag(uno, CMD_GET_SWAP_WARM, SWAP_COLUMN)
    .then((r) => {
      if (!r.answered) return undefined;
      chipSwap.set(uno, r.value);
      rememberChip(uno, r.value);
      listeners.forEach((fn) => {
        try { fn(uno, cache.get(uno)?.swapped); } catch (e) { /* a listener's fault */ }
      });
      const want = cachedReversed(uno);
      // Only when the column has actually been read — `undefined` is not "0".
      if (want !== undefined && (want ? 1 : 0) !== r.value) healChipSwap(uno, want);
      return r.value;
    })
    .catch(() => undefined)
    .finally(() => { chipAsk.delete(uno); });
  chipAsk.set(uno, p);
  return p;
}

/**
 * The flag the WIRE conversion must use — the XOR at the top of this block.
 * The UI keeps reading the column's own flag, so a reversed light still says
 * "reversed" wherever the firmware is the one handling it.
 */
export const appMustSwap = (uno) => {
  const fixture = !!cachedReversed(uno) ? 1 : 0;
  const chip = cachedChipSwaps(uno) === 1 ? 1 : 0;
  return fixture !== chip;
};

const NO_COLUMN = 'The server does not have the Reverse column yet (light_swap_warm_migration.sql).';

/**
 * Set it, in both places. -> { ok, confirmed, message? }
 *
 * ⚠️ THE COLUMN FIRST, THEN THE WORD. The firmware persists whatever the server
 * last told it, so a word sent to a device whose column still holds the old
 * value is undone at that device's next check (see the header note). And a
 * column written with no word waits for that same check, which is up to twenty
 * minutes of a fixture that is still wrong.
 *
 * `confirmed` is the device's own receipt, and it is also how the app learns
 * this unit swaps for itself — the reply updates `chipSwapsWhite`, so the
 * screen stops double-swapping the moment the firmware takes it over.
 */
export async function setLightReversed(uniqueNumber, swapped, userId) {
  const uno = String(uniqueNumber);
  const want = swapped ? 1 : 0;
  try {
    const res = await updateDeviceInstalled(uno, { [SWAP_COLUMN]: want });
    const d = res && res.data;
    // The generic update answers a missing column with an error object, not a throw.
    if (d && (d.err || d.status === '500' || d.message === 'some error occured')) {
      return { ok: false, confirmed: false, message: NO_COLUMN };
    }
  } catch (e) {
    if (e && e.response && e.response.status === 500) {
      return { ok: false, confirmed: false, message: NO_COLUMN };
    }
    return { ok: false, confirmed: false, message: 'Could not save. Try again.' };
  }

  cache.set(uno, { ...(cache.get(uno) || {}), swapped: !!swapped });
  listeners.forEach((fn) => { try { fn(uno, !!swapped); } catch (e) { /* a listener's fault */ } });

  const word = swapped ? CMD_SWAP_WARM_ON : CMD_SWAP_WARM_OFF;
  const ack = await setDeviceFlag(uno, word, CMD_GET_SWAP_WARM, SWAP_COLUMN, { userId });
  if (ack.answered) {
    // Whatever it reported, including a value that did NOT take: the XOR above
    // is what keeps the light right either way, and it needs the truth.
    chipSwap.set(uno, ack.value);
    rememberChip(uno, ack.value);
    asked.add(uno);
    // Re-notify: the wire flag has just changed under the screens as well.
    listeners.forEach((fn) => { try { fn(uno, !!swapped); } catch (e) { /* a listener's fault */ } });
  }
  return { ok: true, confirmed: ack.answered && ack.value === want };
}

/** Test hook. */
export const _resetLightReverse = () => {
  cache.clear();
  inflight.clear();
  chipSwap.clear();
  chipAsk.clear();
  asked.clear();
  healed.clear();
  // A fresh launch, which is what this hook stands in for: the remembered YESes
  // come back, nothing else does.
  seedFromStore();
};
